import { setCookie, getCookie, removeCookie } from 'helpers/cookie';

/**
 * Swapping the browser's identity for an administrator's "view as" session, and
 * swapping it back.
 *
 * THE RISK THIS FILE MANAGES. Impersonation replaces the tokens in the browser.
 * If the administrator's own tokens are not safely put aside first, ending the
 * session leaves them signed in as somebody else with no way back except signing
 * in again — and, worse, they may not notice at all and keep working under a
 * colleague's name.
 *
 * So the originals are saved BEFORE anything is overwritten, under keys that do
 * not collide with the live ones, and the banner that shows while impersonating
 * is driven from the same place.
 */

const KEYS = ['access_token', 'refresh_token', 'token_expiration'];
const BACKUP_PREFIX = 'impersonation_origin_';
const CONTEXT_KEY = 'impersonation_context';

const writeToken = (name, value) => {
  localStorage.setItem(name, value);
  setCookie(name, value, { httpOnly: false, secure: false, sameSite: 'lax' });
};

const readToken = (name) => getCookie(name) || localStorage.getItem(name);

/** Who, if anyone, is currently being impersonated. */
export const impersonationContext = () => {
  try {
    const raw = localStorage.getItem(CONTEXT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const isImpersonating = () => Boolean(impersonationContext());

/**
 * Installs the impersonation tokens, keeping the administrator's own so they can
 * come back.
 *
 * Refuses to start a second impersonation on top of a first: the backup keys
 * hold exactly one identity, and overwriting them would strand the real account
 * with no route home.
 */
export const startImpersonation = ({ tokens, target, actor }) => {
  if (isImpersonating()) {
    throw new Error('Already viewing as somebody else. Stop that session first.');
  }

  KEYS.forEach((name) => {
    const current = readToken(name);
    if (current) localStorage.setItem(`${BACKUP_PREFIX}${name}`, current);
  });

  localStorage.setItem(CONTEXT_KEY, JSON.stringify({
    target, actor, startedAt: new Date().toISOString(),
  }));

  writeToken('access_token', tokens.access_token);
  writeToken('refresh_token', tokens.refresh_token);
  writeToken('token_expiration', String(tokens.token_expiration));
};

/**
 * Puts the administrator back.
 *
 * Returns false when there is nothing to restore — a backup that went missing
 * (cleared storage, a second tab that already stopped) must produce a signed-out
 * state rather than leaving the browser holding somebody else's session.
 */
export const stopImpersonation = () => {
  // Already stopped (another tab got there first): the live tokens are the
  // administrator's again — leave them. Wiping them here signed the admin out
  // whenever a stale banner's Stop was pressed (2026-10-29).
  if (!isImpersonating()) return true;

  const backups = KEYS.map((name) => [name, localStorage.getItem(`${BACKUP_PREFIX}${name}`)]);
  const recoverable = backups.every(([, value]) => Boolean(value));

  KEYS.forEach((name) => localStorage.removeItem(`${BACKUP_PREFIX}${name}`));
  localStorage.removeItem(CONTEXT_KEY);

  if (!recoverable) {
    KEYS.forEach((name) => {
      localStorage.removeItem(name);
      removeCookie(name);
    });
    return false;
  }

  backups.forEach(([name, value]) => writeToken(name, value));
  return true;
};

/**
 * The view-as session ended on its own (two hours, or refused on refresh): go back
 * to the administrator rather than to the sign-in page. True when restored.
 */
export const returnToAdministrator = () => isImpersonating() && stopImpersonation();

/**
 * Calls `callback` when view-as starts or stops in ANOTHER tab — the tokens are
 * shared, so a tab still drawn as the previous identity must reload. Returns the
 * unsubscribe function.
 */
export const onImpersonationChange = (callback) => {
  const listener = (event) => { if (event.key === CONTEXT_KEY) callback(event); };
  window.addEventListener('storage', listener);
  return () => window.removeEventListener('storage', listener);
};
